import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [loading, setLoading] = useState(true);

  // Load theme from localStorage first (for immediate render), then sync with server
  useEffect(() => {
    const savedTheme = localStorage.getItem('qindex_theme') || 'light';
    setTheme(savedTheme);
    applyTheme(savedTheme);
    setLoading(false);
  }, []);

  // Sync theme with server when user is authenticated
  const syncThemeFromServer = async (token) => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/user/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data?.theme) {
        const serverTheme = response.data.theme;
        setTheme(serverTheme);
        localStorage.setItem('qindex_theme', serverTheme);
        applyTheme(serverTheme);
      }
    } catch (error) {
      // Server preference not available, use local
      console.log('Using local theme preference');
    }
  };

  // Save theme to server
  const saveThemeToServer = async (token, newTheme) => {
    if (!token) return;
    
    try {
      await axios.put(`${API}/user/preferences`, 
        { theme: newTheme },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  };

  const applyTheme = (newTheme) => {
    const root = document.documentElement;
    
    if (newTheme === 'dark') {
      root.classList.add('dark');
      // Apply dark mode CSS variables
      root.style.setProperty('--bg-primary', '#1A1614');
      root.style.setProperty('--bg-secondary', '#252220');
      root.style.setProperty('--bg-card', '#2C2826');
      root.style.setProperty('--bg-hover', '#363230');
      root.style.setProperty('--text-primary', '#F5F3F1');
      root.style.setProperty('--text-secondary', '#B8B4B0');
      root.style.setProperty('--text-muted', '#8C8884');
      root.style.setProperty('--border-color', '#3D3935');
      root.style.setProperty('--accent-color', '#D99A6C');
    } else {
      root.classList.remove('dark');
      // Apply light mode CSS variables
      root.style.setProperty('--bg-primary', '#F9F7F5');
      root.style.setProperty('--bg-secondary', '#FFFFFF');
      root.style.setProperty('--bg-card', '#FFFFFF');
      root.style.setProperty('--bg-hover', '#F5F3F1');
      root.style.setProperty('--text-primary', '#2C2420');
      root.style.setProperty('--text-secondary', '#5D524D');
      root.style.setProperty('--text-muted', '#8C8078');
      root.style.setProperty('--border-color', '#E6E0D9');
      root.style.setProperty('--accent-color', '#4A3B32');
    }
  };

  const toggleTheme = (token) => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('qindex_theme', newTheme);
    applyTheme(newTheme);
    
    // Save to server if authenticated
    if (token) {
      saveThemeToServer(token, newTheme);
    }
  };

  const setThemeMode = (newTheme, token) => {
    setTheme(newTheme);
    localStorage.setItem('qindex_theme', newTheme);
    applyTheme(newTheme);
    
    // Save to server if authenticated
    if (token) {
      saveThemeToServer(token, newTheme);
    }
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      toggleTheme, 
      setThemeMode,
      syncThemeFromServer,
      isDark: theme === 'dark',
      loading 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
